import React, { useEffect } from "react";
import { Alert, Button, Card, Space, Table, Tag } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { 
  useGracePeriodStatus, 
  useTriggerBillingChecks,
  useItemsWithoutLocation 
} from "~/services/inventory-billing";
import { GracePeriodItem } from "../types";

export const GracePeriodTab: React.FC = () => {
  const {
    data: gracePeriodData,
    isLoading,
    refetch: refetchGracePeriodStatus,
  } = useGracePeriodStatus();
  
  const {
    data: itemsWithoutLocationData,
    isLoading: isLoadingItemsWithoutLocation,
    refetch: refetchItemsWithoutLocation,
  } = useItemsWithoutLocation();
  
  const triggerBillingChecksMutation = useTriggerBillingChecks();

  // Load grace period data when component mounts
  useEffect(() => {
    refetchGracePeriodStatus();
    refetchItemsWithoutLocation();
  }, [refetchGracePeriodStatus, refetchItemsWithoutLocation]);

  const handleTriggerBillingChecks = async () => {
    try {
      await triggerBillingChecksMutation.mutateAsync();
      // Show success message and refresh data
      refetchGracePeriodStatus();
      refetchItemsWithoutLocation();
    } catch (error) {
      console.error("Error triggering billing checks:", error);
    }
  };

  const handleRefreshAll = () => {
    refetchGracePeriodStatus();
    refetchItemsWithoutLocation();
  };

  const gracePeriodColumns = [
    {
      title: "Inventory Ref",
      dataIndex: "inventoryRef",
      key: "inventoryRef",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string) => title || "N/A",
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Location",
      key: "location",
      render: (record: GracePeriodItem) => {
        if (record.locationName) {
          return (
            <div>
              <div className="font-medium">{record.locationName}</div>
              {record.locationRef && (
                <div className="text-xs text-gray-500">{record.locationRef}</div>
              )}
            </div>
          );
        }
        return <Tag color="red">No Location</Tag>;
      },
    },
    {
      title: "Status Date",
      dataIndex: "availableOnHandDate",
      key: "availableOnHandDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Days in Status",
      dataIndex: "daysInStatus",
      key: "daysInStatus",
      render: (days: number) => `${days} days`,
    },
    {
      title: "Grace Period End",
      dataIndex: "gracePeriodEnd",
      key: "gracePeriodEnd",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      key: "status",
      render: (record: GracePeriodItem) => {
        if (record.isInGracePeriod) {
          return <Tag color="orange">Grace Period</Tag>;
        } else {
          return (
            <Tag color={record.hasBillingPeriod ? "green" : "red"}>
              {record.hasBillingPeriod ? "Billing Active" : "Billing Pending"}
            </Tag>
          );
        }
      },
    },
    {
      title: "Auto Invoice Status",
      key: "invoiceStatus",
      render: (record: GracePeriodItem) => {
        if (record.isInGracePeriod) {
          return <Tag color="orange">Auto Create After Grace</Tag>;
        } else if (record.hasBillingPeriod) {
          return <Tag color="green">Invoice Created</Tag>;
        } else {
          return <Tag color="gray">Pending</Tag>;
        }
      },
    },
  ];

  const itemsWithoutLocationColumns = [
    {
      title: "Inventory Ref",
      dataIndex: "inventoryRef",
      key: "inventoryRef",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string) => title || "N/A",
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Status Date",
      dataIndex: "availableOnHandDate",
      key: "availableOnHandDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Days in Status",
      dataIndex: "daysInStatus",
      key: "daysInStatus",
      render: (days: number) => `${days} days`,
    },
    {
      title: "Current Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "In Grace Period" ? "orange" : "red"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Issue",
      dataIndex: "issue",
      key: "issue",
      render: (issue: string) => (
        <Tag color="red" icon={<WarningOutlined />}>
          {issue}
        </Tag>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card
        title="Grace Period Status"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshAll}
              loading={isLoading || isLoadingItemsWithoutLocation}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<ClockCircleOutlined />}
              onClick={handleTriggerBillingChecks}
              loading={triggerBillingChecksMutation.isPending}
            >
              Trigger Billing Checks
            </Button>
          </Space>
        }
      >
        <div className="mb-4">
          <Alert
            message="Grace Period Management"
            description="Items in AVAILABLE_ON_HAND status have a 3-day grace period before billing starts. Invoices are only created for items that are 'Ready for Billing' (past grace period). Note: Only inventory items with an assigned location can be billed, as billing rates are based on location storage settings."
            type="info"
            showIcon
          />
        </div>

        {/* Items Without Location Warning */}
        {itemsWithoutLocationData && itemsWithoutLocationData.totalCount > 0 && (
          <div className="mb-4">
            <Alert
              message="Items Without Location Assignment"
              description={`${itemsWithoutLocationData.totalCount} inventory items in AVAILABLE_ON_HAND status do not have a location assigned. These items cannot be billed until they are assigned to a location with storage rate settings.`}
              type="warning"
              showIcon
              action={
                <Button
                  size="small"
                  onClick={() => {
                    // You can add navigation to inventory management page here
                    console.log("Navigate to inventory management");
                  }}
                >
                  Manage Locations
                </Button>
              }
            />
          </div>
        )}

        {gracePeriodData && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card size="small" className="border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Grace Period</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {gracePeriodData.itemsInGracePeriod.length}
                    </p>
                  </div>
                  <ExclamationCircleOutlined className="text-2xl text-orange-400" />
                </div>
              </Card>

              <Card size="small" className="border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Ready for Billing</p>
                    <p className="text-2xl font-bold text-green-600">
                      {gracePeriodData.itemsPastGracePeriod?.length || 0}
                    </p>
                  </div>
                  <CheckCircleOutlined className="text-2xl text-green-400" />
                </div>
              </Card>

              <Card size="small" className="border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">No Location</p>
                    <p className="text-2xl font-bold text-red-600">
                      {itemsWithoutLocationData?.totalCount || 0}
                    </p>
                  </div>
                  <WarningOutlined className="text-2xl text-red-400" />
                </div>
              </Card>
            </div>

            {/* Items Without Location */}
            {itemsWithoutLocationData && itemsWithoutLocationData.totalCount > 0 && (
              <Card size="small" title="Items Without Location Assignment" className="border-l-4 border-l-red-500">
                <Table
                  dataSource={itemsWithoutLocationData.itemsWithoutLocation}
                  columns={itemsWithoutLocationColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            )}

            {/* Items in Grace Period */}
            <Card size="small" title="Items in Grace Period">
              {gracePeriodData.itemsInGracePeriod.length > 0 ? (
                <Table
                  dataSource={gracePeriodData.itemsInGracePeriod}
                  columns={gracePeriodColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ) : (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <CheckCircleOutlined className="text-4xl text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    All Clear!
                  </h3>
                  <p className="text-gray-600">
                    No items currently in grace period.
                  </p>
                </div>
              )}
            </Card>

            {gracePeriodData.itemsPastGracePeriod && (
              <Card size="small" title="Items Ready for Billing">
                {gracePeriodData.itemsPastGracePeriod.length > 0 ? (
                  <Table
                    dataSource={gracePeriodData.itemsPastGracePeriod}
                    columns={gracePeriodColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <CheckCircleOutlined className="text-4xl text-green-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      All Clear!
                    </h3>
                    <p className="text-gray-600">
                      No items currently ready for billing.
                    </p>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
